import React, { useEffect, useState } from 'react';
import useSiteBranding from '../hooks/useSiteBranding';
import usePublicContactDisplay from '../hooks/usePublicContactDisplay';
import { getTelHrefFromDisplayPhone } from '../utils/publicContactInfo';
import { buildWhatsAppUrl, getWhatsAppChatHelpPhoneDigits } from '../utils/whatsappContact';
import TrustSupportBanner from '../components/TrustSupportBanner';
import './TrustPages.css';

const PAGE_TITLE = 'Contact Us';

export default function ContactPage() {
  const { siteName } = useSiteBranding();
  const { phoneDisplay, email, socialLinks } = usePublicContactDisplay();
  const { twitterUrl, facebookUrl, instagramUrl, tiktokUrl } = socialLinks || {};
  const hasSocial = !!(twitterUrl || facebookUrl || instagramUrl || tiktokUrl);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');

  const brand = siteName || 'Super Wear';

  useEffect(() => {
    document.title = `${PAGE_TITLE} — ${brand}`;
  }, [brand]);

  const telHref = getTelHrefFromDisplayPhone(phoneDisplay);

  const handleSubmit = (e) => {
    e.preventDefault();
    const digits = getWhatsAppChatHelpPhoneDigits();
    if (!digits) {
      window.alert('WhatsApp number is not set yet. Please call or email us using the details above.');
      return;
    }
    const text = [
      `Message from ${brand} website`,
      '',
      `Name: ${name.trim() || '(not provided)'}`,
      `Phone: ${phone.trim() || '(not provided)'}`,
      '',
      message.trim(),
    ].join('\n');
    const url = buildWhatsAppUrl(digits, text);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="trust-page">
      <div className="trust-page__inner">
        <header className="trust-page__hero">
          <p className="trust-page__hero-eyebrow">We&apos;re here to help</p>
          <h1 className="trust-page__title">{PAGE_TITLE}</h1>
          <p className="trust-page__subtitle">
            Questions about an order, sizing, or a product? Reach us by phone, email, or send a message with the form
            below (opens WhatsApp with your details filled in). <strong>Orders and support are open 24/7.</strong>
          </p>
        </header>

        <TrustSupportBanner />

        <div className="contact-grid">
          <div className="contact-card">
            <h3>Phone</h3>
            <p className="contact-label">Call us</p>
            <p className="contact-value">
              <a href={telHref}>{phoneDisplay}</a>
            </p>
          </div>

          <div className="contact-card">
            <h3>Email</h3>
            <p className="contact-label">Write to us</p>
            <p className="contact-value">
              <a href={`mailto:${email}`}>{email}</a>
            </p>
          </div>

          {hasSocial ? (
            <div className="contact-card contact-card--social" style={{ gridColumn: '1 / -1' }}>
              <h3>Social media</h3>
              <p className="contact-label">Follow us</p>
              <div className="contact-social-links">
                {twitterUrl ? (
                  <a href={twitterUrl} target="_blank" rel="noopener noreferrer">
                    <span className="contact-social-icon" aria-hidden>
                      🐦
                    </span>
                    Twitter / X
                  </a>
                ) : null}
                {facebookUrl ? (
                  <a href={facebookUrl} target="_blank" rel="noopener noreferrer">
                    <span className="contact-social-icon" aria-hidden>
                      📘
                    </span>
                    Facebook
                  </a>
                ) : null}
                {instagramUrl ? (
                  <a href={instagramUrl} target="_blank" rel="noopener noreferrer">
                    <span className="contact-social-icon" aria-hidden>
                      📷
                    </span>
                    Instagram
                  </a>
                ) : null}
                {tiktokUrl ? (
                  <a href={tiktokUrl} target="_blank" rel="noopener noreferrer">
                    <span className="contact-social-icon" aria-hidden>
                      <i className="fab fa-tiktok" aria-hidden />
                    </span>
                    TikTok
                  </a>
                ) : null}
              </div>
            </div>
          ) : null}

          <div className="contact-card" style={{ gridColumn: '1 / -1' }}>
            <h3>Send a message</h3>
            <form className="contact-form" onSubmit={handleSubmit}>
              <label htmlFor="contact-name">Name</label>
              <input
                id="contact-name"
                type="text"
                autoComplete="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                required
              />
              <label htmlFor="contact-phone">Phone</label>
              <input
                id="contact-phone"
                type="tel"
                autoComplete="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Your phone number"
              />
              <label htmlFor="contact-message">Message</label>
              <textarea
                id="contact-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="How can we help?"
                required
              />
              <button type="submit" className="contact-wa-btn">
                <i className="fab fa-whatsapp" aria-hidden />
                Send on WhatsApp
              </button>
            </form>
            <p className="contact-hint">
              Opens WhatsApp (same number as the site chat button) with your name, phone, and message ready to send. For
              order issues, add your order number in the message.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
